import { Injector } from '@angular/core';

import { SchemaNode } from '../model/schema-node.types';
import { SchemaReference } from '../model/schema-reference.types';
import { NO_DOCUMENT_LOADER, resolveSchemaReference } from './compose-schema';
import { reportUnresolved } from './report-unresolved';
import { SCHEMA_DOCUMENT_LOADER } from './schema-document-loader.token';
import { SCHEMA_REFERENCE_CHAIN } from './schema-reference-chain.token';

const NO_CHAIN = (): readonly string[] => [];

/**
 * The part `reference` stands for, resolved now as a node outlet under `injector` resolves it: through the
 * `SCHEMA_DOCUMENT_LOADER` provided there, its eager references composed, and with the keys of the documents resolved
 * above, so a reference back to one of them fails as a cycle. Once the part fails, it resolves to the reference's
 * `$refStates.error` instead, and dev mode warns as the node outlet does; without one it rejects, and the host decides
 * what that means. For a host that holds a place until the part has arrived, as the modal host does (672307).
 */
export async function resolveSchemaPart(reference: SchemaReference, injector: Injector, abortSignal?: AbortSignal): Promise<SchemaNode> {
  const documentLoader = injector.get(SCHEMA_DOCUMENT_LOADER, null) ?? NO_DOCUMENT_LOADER;
  const chainAbove = injector.get(SCHEMA_REFERENCE_CHAIN, null) ?? NO_CHAIN;

  try {
    return await resolveSchemaReference(reference, documentLoader, chainAbove(), abortSignal);
  } catch (failure: unknown) {
    const error = reference.$refStates?.error;

    if (!error || abortSignal?.aborted) {
      throw failure;
    }

    reportUnresolved(reference, failure);

    return error;
  }
}

/**
 * The injector to render the part `reference` stands for under: below `injector`, with the part's key added to the
 * documents resolved above, so a reference back to the part inside it fails as a cycle, as it does below a node outlet
 * (672307).
 */
export function schemaPartInjector(reference: SchemaReference, injector: Injector): Injector {
  const chainAbove = injector.get(SCHEMA_REFERENCE_CHAIN, null) ?? NO_CHAIN;

  return Injector.create({
    providers: [{ provide: SCHEMA_REFERENCE_CHAIN, useValue: (): readonly string[] => [...chainAbove(), reference.$ref] }],
    parent: injector,
  });
}
